/**
 * Another client's identifiers in a library document: what was found
 * (masked) and the two ways out, an anonymized copy beside the original or
 * keeping it as is on purpose. Kept on purpose, it says nothing more.
 */
import { ArrowRight, Loader2, ShieldAlert, ShieldCheck } from "lucide-react";
import type { FC } from "react";

import { Button } from "@/components/ui/button";
import { Tag } from "@/components/ui/tag";

import type { FirmLibraryEntry } from "../types";
import { EntryCallout } from "./EntryCallout";

type Props = {
  entry: FirmLibraryEntry;
  canEdit: boolean;
  busy: boolean;
  anonymizing: boolean;
  onAnonymize: () => void;
  onKeep: () => void;
  onOpenEntry: (entryId: string) => void;
};

const kindsSentence = (kinds: string[]): string => {
  if (kinds.length === 0) return "client details";
  if (kinds.length === 1) return kinds[0];
  return `${kinds.slice(0, -1).join(", ")} and ${kinds[kinds.length - 1]}`;
};

/** For the header's meta line: this entry is an anonymized copy. */
export const AnonymizedCopyNote: FC<{
  entry: FirmLibraryEntry;
  onOpenEntry: (entryId: string) => void;
}> = ({ entry, onOpenEntry }) =>
  entry.anonymized_from ? (
    <span
      className="flex items-center gap-1"
      data-testid="anonymized-copy-note"
    >
      <ShieldCheck className="size-3.5 text-success" aria-hidden />
      Anonymized copy
      <button
        type="button"
        className="inline-flex items-center gap-0.5 text-fg-2 underline-offset-2 hover:underline"
        onClick={() => onOpenEntry(entry.anonymized_from as string)}
      >
        Open the original
        <ArrowRight className="size-3" />
      </button>
    </span>
  ) : null;

export const ClientDetailsBanner: FC<Props> = ({
  entry,
  canEdit,
  busy,
  anonymizing,
  onAnonymize,
  onKeep,
  onOpenEntry
}) => {
  const details = entry.client_details;
  const copyId = details?.anonymized_copy_id ?? null;
  if (entry.anonymized_from || details?.status !== "found") return null;

  return (
    <EntryCallout
      tone="danger"
      icon={ShieldAlert}
      title="This document names a real client"
      data-testid="client-details-banner"
      aria-label="Client details in this document"
      actions={
        <>
          {canEdit && entry.authorable ? (
            <Button
              variant="primary"
              disabled={busy || anonymizing}
              onClick={onAnonymize}
              data-testid="anonymize-entry"
            >
              {anonymizing ? <Loader2 className="animate-spin" /> : null}
              {copyId
                ? "Make Another Anonymized Copy"
                : "Make an Anonymized Copy"}
            </Button>
          ) : null}
          {canEdit ? (
            <Button
              variant="ghost"
              disabled={busy || anonymizing}
              onClick={onKeep}
              data-testid="keep-client-details"
            >
              Keep as Is
            </Button>
          ) : null}
          {copyId ? (
            <Button
              variant="link"
              onClick={() => onOpenEntry(copyId)}
              data-testid="open-anonymized-copy"
            >
              Open the Anonymized Copy
              <ArrowRight />
            </Button>
          ) : null}
        </>
      }
    >
      <p>
        It contains {kindsSentence(details.kinds)} from another matter
        {details.count > 1 ? ` (${details.count} places)` : ""}. A copy with
        placeholders is safer to draft from.
        {canEdit && !entry.authorable
          ? " Use the Word copy of this PDF to make one."
          : ""}
      </p>
      {details.examples.length ? (
        <ul className="flex flex-wrap gap-1">
          {details.examples.map((example) => (
            <li key={example}>
              <Tag mono>{example}</Tag>
            </li>
          ))}
        </ul>
      ) : null}
    </EntryCallout>
  );
};
